import { PageType, StaticRoutes, GetRouteAdTargeting } from 'common/routing/page-definition'

import { layout } from 'thewest/App.routing'
import { metrics } from 'component-library/__styling/settings'

import { Topic } from 'common/client-data-types/topic'
import { getTopicSponsor } from 'thewest/routes/topic/get-topic-sponsor'

import { getLatestRouteInfo } from 'thewest/routes/topic/custom/get-latest-route-info'
import { getBusinessRouteInfo } from 'thewest/routes/topic/custom/get-business-route-info'
import { getEntertainmentRouteInfo } from 'thewest/routes/topic/custom/get-entertainment-route-info'
import { getNewsRouteInfo } from 'thewest/routes/topic/custom/get-news-route-info'
import { getOpinionRouteInfo } from 'thewest/routes/topic/custom/get-opinion-route-info'
import { getPoliticsRoute } from 'thewest/routes/topic/custom/get-politics-route'
import { getLifestyleRouteInfo } from 'thewest/routes/topic/custom/get-lifestyle-route-info'
import { getSportRouteInfo } from 'thewest/routes/topic/custom/get-sport-route-info'
import { getTechnologyRouteInfo } from 'thewest/routes/topic/custom/get-technology-route-info'
import { getTravelRouteInfo } from 'thewest/routes/topic/custom/get-travel-route-info'
import { getRegionalRouteInfo } from 'thewest/routes/topic/custom/get-regional-route-info'
import { getAFLRouteInfo } from 'thewest/routes/topic/custom/get-afl-route-info'
import { getInsideCoverRouteInfo } from 'thewest/routes/topic/custom/get-insidecover-route-info'

import { BaseClientConfig } from 'common/client/BaseClientConfig'
import { getClaremontSerialKillingsRouteInfo } from './custom/get-claremont-serial-killings-route-info'
import { getSubscriberFeaturesRouteInfo } from './custom/get-subscriber-features-route-info'
import { SierraRouteProps } from 'component-library/collections-new/Sierra/Sierra.routing'
import { TheWestProps as LimaRouteProps } from 'component-library/collections-new/Lima/Lima.routing'
import { ContentDataTypes } from 'common/data/loader-definitions'
import { getKerryStokesCollectionRouteInfo } from './custom/get-kerry-stokes-collection-route-info'
import { getYourMoneyRouteInfo } from './custom/get-your-money-route-info'
import { getCricketRouteInfo } from './custom/get-cricket-route-info'
import { getGerardRossRouteInfo } from './custom/get-gerard-ross-route-info'
import { topicParallax } from './topic-parallax'
import { getAFLDraftRouteInfo } from './custom/get-afl-draft-route'
import { getRTS2019RouteInfo } from './custom/get-rts-2019-route'
import { getCarOfTheYear2019RouteInfo } from './custom/get-car-of-the-year-2019-route-info'
import { AdTargeting } from 'common/advertising/add-ads'
import { TheWestSection } from 'component-library/__styling/settings/sections/thewest'

interface TopicSectionBuilderHooks {
    sierra?: SierraRouteProps & { dataDefinitionArgs: ContentDataTypes }
    lima?: LimaRouteProps & { dataDefinitionArgs: ContentDataTypes }
}

export interface TopicSectionBuilder {
    getCuratedSection: (hooks?: TopicSectionBuilderHooks) => typeof layout._componentType[]
}

export type GetMainSectionFn = (
    topicSectionBuilder: TopicSectionBuilder
) => typeof layout._componentType[]

const createPage = ({
    topic,
    pageTitle,
    getMainSection,
    curationName,
    section = 'default',
    getAdTargeting,
}: {
    config: BaseClientConfig
    topic: Topic
    pageTitle: string
    getMainSection: GetMainSectionFn
    curationName?: string
    section?: TheWestSection
    getAdTargeting: GetRouteAdTargeting
}): PageType<typeof layout, TheWestSection> => {
    const { adUnitPath, ssAdUnits, topics } = getAdTargeting('home', section, topic)

    const adTargeting: AdTargeting = {
        pageId: topic.title,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    const getCuratedSection: TopicSectionBuilder['getCuratedSection'] = hooks => {
        const sierraHookProps = hooks ? hooks.sierra || {} : {}
        const limaHookProps = hooks ? hooks.lima || {} : {}

        return [
            layout.component({
                type: 'sierra',
                props: {
                    verticalSpacing: 'gridGap',
                    fixedRatios: ['16:9'],
                    heroImageLayout: 'fill-container-horizontally',
                    cardOrientation: { type: 'Landscape-Portrait', maxBreakpoint: 'md' },
                    cardLayout: ['hero', 'commonWithBackground'],
                    dataDefinitionArgs: {
                        type: 'curation',
                        name: curationName || topic.id,
                        offset: 0,
                        pageSize: 2,
                    },
                    ...sierraHookProps,
                },
            }),
            {
                type: 'lima',
                props: {
                    verticalSpacing: 'md',
                    fontScale: 1.2,
                    fixedRatios: ['16:9'],
                    hasBackground: true,
                    initialColumns: 1,
                    intermediateColumns: 2,
                    finalColumns: 4,
                    cardOrientation: { type: 'Landscape-Portrait' },

                    dataDefinitionArgs: {
                        type: 'curation',
                        name: curationName || topic.id,
                        offset: 2,
                        pageSize: 4,
                    },
                    ...limaHookProps,
                },
            },
            ...topicParallax(),
        ]
    }

    return {
        kind: 'page',
        heading: pageTitle,
        pageType: 'home',
        adTargeting,
        hideHeading: true,
        includeFromSectionInMetaDescription: !topic.seoDescription,
        pageMeta: {
            description: topic.seoDescription || `The Latest in ${topic.seoTitle}`,
        },
        section,
        compositions: [
            layout.composition({
                type: 'box',
                props: {
                    containerWidth: metrics.thewest.mainContentWidth,
                    horizontalGutters: 'outerMargin',
                    verticalGutters: ['outerMargin', 'unset'],
                },
                contentAreas: {
                    main: [
                        layout.component({
                            type: 'breaking-news',
                            props: {
                                dataDefinitionArgs: {
                                    type: 'curation',
                                    name: 'breaking-news',
                                    offset: 0,
                                    pageSize: 1,
                                },
                            },
                        }),
                        layout.component({
                            type: 'breadcrumb',
                            props: {
                                topic,
                                sponsor: getTopicSponsor(topic),
                                enableHeaderTag: true,
                            },
                        }),
                    ],
                },
            }),
            layout.composition({
                type: 'thor',
                props: {
                    containerWidth: metrics.thewest.mainContentWidth,
                    horizontalGutters: true,
                },
                contentAreas: {
                    main: getMainSection({ getCuratedSection }),
                },
            }),
        ],
    }
}

export const customTopicRoutes: StaticRoutes<typeof layout, TheWestSection> = {
    '/latest': getLatestRouteInfo({
        id: 'latest',
        title: 'Latest News',
        seoTitle: 'Latest News',
        metadata: {},
    }),
    '/features': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'features',
                title: 'Subscriber Exclusive',
                seoTitle: 'Subscriber Exclusive',
                metadata: {},
            },
            pageTitle: 'Subscriber Exclusive',
            getMainSection: getSubscriberFeaturesRouteInfo,
            curationName: 'features',
            getAdTargeting,
        }),
    '/business': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'business', title: 'Business', seoTitle: 'Business', metadata: {} },
            pageTitle: 'Business',
            getMainSection: getBusinessRouteInfo,
            getAdTargeting,
        }),
    '/entertainment': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'entertainment',
                title: 'Entertainment',
                seoTitle: 'Entertainment',
                metadata: {},
            },
            pageTitle: 'Entertainment',
            getMainSection: getEntertainmentRouteInfo,
            getAdTargeting,
        }),
    '/lifestyle': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'lifestyle', title: 'Lifestyle', seoTitle: 'Lifestyle', metadata: {} },
            pageTitle: 'Lifestyle',
            getMainSection: getLifestyleRouteInfo,
            getAdTargeting,
        }),
    '/news': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'news', title: 'News', seoTitle: 'News', metadata: {} },
            pageTitle: 'News',
            getMainSection: getNewsRouteInfo,
            getAdTargeting,
        }),
    '/opinion': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'opinion', title: 'Opinion', seoTitle: 'Opinion', metadata: {} },
            pageTitle: 'Opinion',
            getMainSection: getOpinionRouteInfo,
            getAdTargeting,
        }),
    '/opinion/inside-cover': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'opinion/inside-cover',
                title: 'Inside Cover',
                seoTitle: 'Inside Cover',
                metadata: {},
            },
            pageTitle: 'Inside Cover',
            getMainSection: getInsideCoverRouteInfo,
            getAdTargeting,
        }),
    '/politics': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'politics', title: 'Politics', seoTitle: 'Politics', metadata: {} },
            pageTitle: 'Politics',
            getMainSection: getPoliticsRoute,
            getAdTargeting,
        }),
    '/sport': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'sport', title: 'Sport', seoTitle: 'Sport', metadata: {} },
            pageTitle: 'Sport',
            getMainSection: getSportRouteInfo,
            getAdTargeting,
        }),
    '/technology': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'technology', title: 'Technology', seoTitle: 'Technology', metadata: {} },
            pageTitle: 'Technology',
            getMainSection: getTechnologyRouteInfo,
            getAdTargeting,
        }),
    '/travel': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'travel', title: 'Travel', seoTitle: 'Travel', metadata: {} },
            pageTitle: 'Travel',
            getMainSection: getTravelRouteInfo,
            getAdTargeting,
        }),
    '/news/regional': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'news/regional',
                title: 'Regional',
                seoTitle: 'Regional',
                metadata: {},
            },
            pageTitle: 'Regional',
            getMainSection: getRegionalRouteInfo,
            curationName: 'regional',
            getAdTargeting,
        }),
    '/news/claremont-serial-killings': getClaremontSerialKillingsRouteInfo({
        id: 'news/claremont-serial-killings',
        title: 'Claremont Serial Killings',
        seoTitle: 'Claremont Serial Killings',
        metadata: {},
        parent: { id: 'news', title: 'News', seoTitle: 'News', metadata: {} },
    }),
    '/sport/afl': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'afl', title: 'AFL', seoTitle: 'AFL', metadata: {} },
            pageTitle: 'AFL',
            getMainSection: getAFLRouteInfo,
            getAdTargeting,
        }),
    '/sport/afl-draft': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: { id: 'afl-draft', title: 'AFL Draft', seoTitle: 'AFL Draft', metadata: {} },
            pageTitle: 'AFL Draft',
            getMainSection: getAFLDraftRouteInfo,
            getAdTargeting,
        }),
    '/features/kerry-stokes-collection': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'features/kerry-stokes-collection',
                title: 'The Kerry Stokes Collection',
                seoTitle: 'The Kerry Stokes Collection',
                metadata: {},
                parent: {
                    id: 'features',
                    title: 'Features',
                    metadata: {},
                    seoTitle: 'Features',
                },
            },
            pageTitle: 'The Kerry Stokes Collection',
            getMainSection: getKerryStokesCollectionRouteInfo,
            getAdTargeting,
        }),
    '/business/your-money': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'your-money',
                title: 'Your Money',
                seoTitle: 'Your Money',
                metadata: {},
                parent: { id: 'business', title: 'Business', seoTitle: 'Business', metadata: {} },
            },
            pageTitle: 'Your Money',
            getMainSection: getYourMoneyRouteInfo,
            getAdTargeting,
        }),
    '/sport/cricket': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'sport/cricket',
                title: 'Cricket',
                seoTitle: 'Cricket',
                metadata: {},
                parent: { id: 'sport', title: 'Sport', seoTitle: 'Sport', metadata: {} },
            },
            pageTitle: 'Cricket',
            getMainSection: getCricketRouteInfo,
            curationName: 'cricket',
            getAdTargeting,
        }),
    '/features/gerard-ross': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'features/gerard-ross',
                title: 'Gerard Ross',
                seoTitle: 'Gerard Ross',
                metadata: {},
                parent: {
                    id: 'features',
                    title: 'Features',
                    metadata: {},
                    seoTitle: 'Features',
                },
            },
            pageTitle: 'Gerard Ross',
            getMainSection: getGerardRossRouteInfo,
            getAdTargeting,
        }),
    '/features/2019-cars-of-the-year': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'features/2019-cars-of-the-year',
                title: '2019 Cars of the Year',
                seoTitle: '2019 Cars of the Year',
                metadata: {},
                parent: {
                    id: 'features',
                    title: 'Features',
                    metadata: {},
                    seoTitle: 'Features',
                },
            },
            pageTitle: '2019 Cars of the Year',
            getMainSection: getCarOfTheYear2019RouteInfo,
            getAdTargeting,
        }),
    '/business/rts2019': ({ config, getAdTargeting }) =>
        createPage({
            config,
            topic: {
                id: 'rts2019',
                title: 'Resources Technology Showcase 2019',
                seoTitle: 'Resources Technology Showcase 2019',
                metadata: {},
                parent: { id: 'business', title: 'Business', seoTitle: 'Business', metadata: {} },
            },
            pageTitle: 'Resources Technology Showcase 2019',
            getMainSection: getRTS2019RouteInfo,
            getAdTargeting,
        }),
}
